import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="label-accent">{label}</span>
    <input className="hb-input mt-1.5" {...props} />
  </label>
);

export default function AuthPage() {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", business: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(`/auth/${mode}`, payload);
      setUser(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden grain">
        <img src="/brand/lunch.jpg" alt="Hey Boo! members at a networking lunch" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[var(--hb-ink)]/55" />
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="label-accent !text-[var(--hb-ink)] bg-[var(--hb-lime)] self-start px-2.5 py-1 rounded">Unapologetically different</div>
          <h1 className="font-display text-5xl xl:text-6xl mt-4 leading-[1.05] max-w-xl">
            No woman builds alone here.
          </h1>
          <p className="mt-5 max-w-md text-white/80">
            The members-only home for female founders and business owners who are ALL IN.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md rise">
          <img src="/brand/logo.png" alt="Hey Boo!" className="w-32 -ml-3 -rotate-3" data-testid="auth-logo" />
          <div className="label-accent mt-2">Members Only</div>
          <h2 className="font-display text-4xl mt-2" data-testid="auth-heading">
            {mode === "login" ? "BE IN THE ROOM." : "Request your seat."}
          </h2>
          <p className="text-[var(--hb-muted)] mt-2 text-sm">
            {mode === "login"
              ? "Welcome back, boo. Log in to your community."
              : "Create your account. Every new member is personally approved to keep this space safe."}
          </p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <Field label="Your name" value={form.name} onChange={set("name")} required data-testid="register-name-input" />
                <Field label="Business name" value={form.business} onChange={set("business")} data-testid="register-business-input" />
              </>
            )}
            <Field label="Email" type="email" value={form.email} onChange={set("email")} required data-testid="auth-email-input" />
            <Field label="Password" type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="auth-password-input" />
            <button type="submit" disabled={busy} className="btn-primary w-full !py-3.5" data-testid="auth-submit-button">
              {busy ? "One sec..." : mode === "login" ? "Log in" : "Request to join"}
            </button>
          </form>
          <button
            type="button"
            className="mt-6 text-sm text-[var(--hb-muted)] hover:text-[var(--hb-ink)] underline underline-offset-4"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            data-testid="auth-toggle-mode"
          >
            {mode === "login" ? "New here? Request to join" : "Already a member? Log in"}
          </button>
        </div>
      </div>
    </div>
  );
}
